import { useEffect, useMemo, useState } from "react";
import { useLocale } from "../../intl";
import { submitCasaMapaPartnerDataContribution } from "../../services/casamapaPartnerAgencyApi";
import "../../styles/partnerDataContributionWizard.css";

const COPY = "realEstate.casaMapa.admin.partnerListings.contributeWizard";

const METHODS = [
  { id: "ftp", icon: "FTP" },
  { id: "google_sheet", icon: "Sheets" },
  { id: "document", icon: "File" },
  { id: "api", icon: "API" },
  { id: "data_object", icon: "JSON" },
];

const STEPS = ["method", "configure", "review", "done"];

const EMPTY_CONFIG = {
  ftp: {
    protocol: "sftp",
    host: "",
    port: "22",
    username: "",
    password: "",
    remotePath: "/",
    passive: true,
  },
  google_sheet: {
    sheetUrl: "",
    sheetName: "",
    sharedWith: "",
  },
  document: {
    formatHint: "",
    encoding: "utf-8",
  },
  api: {
    baseUrl: "",
    authType: "api_key",
    apiKeyHeader: "Authorization",
    apiKeyValue: "",
    listingsPath: "/listings",
    docsUrl: "",
  },
  data_object: {
    sampleObject: `{
  "externalId": "ABC-123",
  "title": "Apartamento en El Poblado",
  "listingType": "sale",
  "priceCop": 850000000,
  "bedrooms": 3,
  "bathrooms": 2,
  "areaM2": 120,
  "neighborhood": "El Poblado",
  "municipality": "Medellín",
  "latitude": 6.2088,
  "longitude": -75.567,
  "photoUrls": ["https://example.com/photo.jpg"]
}`,
    schemaNotes: "",
    updateCadence: "daily",
  },
};

function readFileAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || "");
      const base64 = result.includes(",") ? result.split(",")[1] : result;
      resolve(base64);
    };
    reader.onerror = () => reject(new Error("Could not read the selected file."));
    reader.readAsDataURL(file);
  });
}

function MethodCard({ methodId, selected, onSelect, t }) {
  return (
    <button
      type="button"
      className={`cm-contrib-wizard__method${selected ? " is-selected" : ""}`}
      onClick={() => onSelect(methodId)}
      aria-pressed={selected}
    >
      <span className="cm-contrib-wizard__method-icon" aria-hidden="true">
        {METHODS.find((row) => row.id === methodId)?.icon}
      </span>
      <strong>{t(`${COPY}.methods.${methodId}.title`)}</strong>
      <span>{t(`${COPY}.methods.${methodId}.body`)}</span>
    </button>
  );
}

function StepDots({ stepIndex, t }) {
  return (
    <ol className="cm-contrib-wizard__steps" aria-label={t(`${COPY}.stepsLabel`)}>
      {STEPS.slice(0, 3).map((stepId, index) => (
        <li
          key={stepId}
          className={
            index === stepIndex ? "is-current" : index < stepIndex ? "is-done" : undefined
          }
        >
          <span>{index + 1}</span>
          {t(`${COPY}.steps.${stepId}`)}
        </li>
      ))}
    </ol>
  );
}

export default function PartnerListingDataContributionWizard({
  open,
  onClose,
  agencies = [],
  defaultAgencySlug = "",
}) {
  const { t } = useLocale();
  const [stepIndex, setStepIndex] = useState(0);
  const [method, setMethod] = useState("");
  const [agencySlug, setAgencySlug] = useState(defaultAgencySlug || "");
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [notes, setNotes] = useState("");
  const [configs, setConfigs] = useState(EMPTY_CONFIG);
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open) return undefined;
    setStepIndex(0);
    setMethod("");
    setAgencySlug(defaultAgencySlug || agencies[0]?.slug || "");
    setContactName("");
    setContactEmail("");
    setNotes("");
    setConfigs(EMPTY_CONFIG);
    setFile(null);
    setSubmitting(false);
    setError("");
    setResult(null);

    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, defaultAgencySlug, agencies, onClose]);

  const stepId = STEPS[stepIndex];
  const activeConfig = configs[method] || {};

  const methodLabel = useMemo(
    () => (method ? t(`${COPY}.methods.${method}.title`) : ""),
    [method, t]
  );

  if (!open) return null;

  function updateConfig(patch) {
    setConfigs((current) => ({
      ...current,
      [method]: { ...current[method], ...patch },
    }));
  }

  function validateConfigure() {
    if (!method) return t(`${COPY}.errors.methodRequired`);
    if (method === "ftp" && !String(activeConfig.host || "").trim()) {
      return t(`${COPY}.errors.ftpHost`);
    }
    if (method === "ftp" && !String(activeConfig.username || "").trim()) {
      return t(`${COPY}.errors.ftpUser`);
    }
    if (method === "google_sheet") {
      const url = String(activeConfig.sheetUrl || "");
      if (!/^https:\/\/docs\.google\.com\/spreadsheets\//i.test(url)) {
        return t(`${COPY}.errors.sheetUrl`);
      }
    }
    if (method === "document" && !file) {
      return t(`${COPY}.errors.fileRequired`);
    }
    if (method === "api" && !/^https?:\/\//i.test(String(activeConfig.baseUrl || ""))) {
      return t(`${COPY}.errors.apiUrl`);
    }
    if (method === "data_object") {
      try {
        JSON.parse(String(activeConfig.sampleObject || ""));
      } catch {
        return t(`${COPY}.errors.sampleJson`);
      }
    }
    return "";
  }

  async function handleNext() {
    setError("");
    if (stepId === "method") {
      if (!method) {
        setError(t(`${COPY}.errors.methodRequired`));
        return;
      }
      setStepIndex(1);
      return;
    }
    if (stepId === "configure") {
      const validationError = validateConfigure();
      if (validationError) {
        setError(validationError);
        return;
      }
      setStepIndex(2);
    }
  }

  async function handleSubmit() {
    setError("");
    setSubmitting(true);
    try {
      const payload = {
        method,
        agencySlug: agencySlug || undefined,
        contactName: contactName || undefined,
        contactEmail: contactEmail || undefined,
        notes: notes || undefined,
        config: { ...activeConfig },
      };
      if (method === "document" && file) {
        payload.file = {
          fileName: file.name,
          mimeType: file.type || "application/octet-stream",
          base64: await readFileAsBase64(file),
        };
      }
      if (method === "ftp") {
        payload.config.port = Number(activeConfig.port) || undefined;
      }
      const body = await submitCasaMapaPartnerDataContribution(payload);
      setResult(body);
      setStepIndex(3);
    } catch (err) {
      setError(err.message || t(`${COPY}.errors.submit`));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="cm-contrib-wizard-backdrop" role="presentation" onClick={onClose}>
      <div
        className="cm-contrib-wizard"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cm-contrib-wizard-title"
        onClick={(event) => event.stopPropagation()}
      >
        <header className="cm-contrib-wizard__header">
          <div>
            <p className="cm-contrib-wizard__eyebrow">{t(`${COPY}.eyebrow`)}</p>
            <h2 id="cm-contrib-wizard-title">{t(`${COPY}.title`)}</h2>
            <p className="muted">{t(`${COPY}.subtitle`)}</p>
          </div>
          <button
            type="button"
            className="cm-contrib-wizard__close"
            onClick={onClose}
            aria-label={t(`${COPY}.close`)}
          >
            ×
          </button>
        </header>

        {stepId !== "done" ? <StepDots stepIndex={stepIndex} t={t} /> : null}

        <div className="cm-contrib-wizard__body">
          {stepId === "method" ? (
            <div className="cm-contrib-wizard__methods" role="list">
              {METHODS.map((row) => (
                <MethodCard
                  key={row.id}
                  methodId={row.id}
                  selected={method === row.id}
                  onSelect={setMethod}
                  t={t}
                />
              ))}
            </div>
          ) : null}

          {stepId === "configure" ? (
            <div className="cm-contrib-wizard__form">
              {agencies.length > 1 ? (
                <label>
                  {t(`${COPY}.fields.agency`)}
                  <select
                    value={agencySlug}
                    onChange={(event) => setAgencySlug(event.target.value)}
                  >
                    <option value="">{t(`${COPY}.fields.agencyAny`)}</option>
                    {agencies.map((agency) => (
                      <option key={agency.slug} value={agency.slug}>
                        {agency.name || agency.slug}
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}

              {method === "ftp" ? (
                <>
                  <label>
                    {t(`${COPY}.fields.protocol`)}
                    <select
                      value={activeConfig.protocol}
                      onChange={(event) =>
                        updateConfig({
                          protocol: event.target.value,
                          port: event.target.value === "sftp" ? "22" : "21",
                        })
                      }
                    >
                      <option value="sftp">SFTP</option>
                      <option value="ftp">FTP</option>
                    </select>
                  </label>
                  <div className="cm-contrib-wizard__row">
                    <label>
                      {t(`${COPY}.fields.host`)}
                      <input
                        value={activeConfig.host}
                        onChange={(event) => updateConfig({ host: event.target.value })}
                        placeholder="ftp.agency.com"
                        autoComplete="off"
                      />
                    </label>
                    <label>
                      {t(`${COPY}.fields.port`)}
                      <input
                        value={activeConfig.port}
                        onChange={(event) => updateConfig({ port: event.target.value })}
                        inputMode="numeric"
                      />
                    </label>
                  </div>
                  <div className="cm-contrib-wizard__row">
                    <label>
                      {t(`${COPY}.fields.username`)}
                      <input
                        value={activeConfig.username}
                        onChange={(event) => updateConfig({ username: event.target.value })}
                        autoComplete="off"
                      />
                    </label>
                    <label>
                      {t(`${COPY}.fields.password`)}
                      <input
                        type="password"
                        value={activeConfig.password}
                        onChange={(event) => updateConfig({ password: event.target.value })}
                        autoComplete="new-password"
                      />
                    </label>
                  </div>
                  <label>
                    {t(`${COPY}.fields.remotePath`)}
                    <input
                      value={activeConfig.remotePath}
                      onChange={(event) => updateConfig({ remotePath: event.target.value })}
                      placeholder="/exports/listings"
                    />
                  </label>
                </>
              ) : null}

              {method === "google_sheet" ? (
                <>
                  <label>
                    {t(`${COPY}.fields.sheetUrl`)}
                    <input
                      value={activeConfig.sheetUrl}
                      onChange={(event) => updateConfig({ sheetUrl: event.target.value })}
                      placeholder="https://docs.google.com/spreadsheets/d/…"
                    />
                  </label>
                  <label>
                    {t(`${COPY}.fields.sheetName`)}
                    <input
                      value={activeConfig.sheetName}
                      onChange={(event) => updateConfig({ sheetName: event.target.value })}
                      placeholder={t(`${COPY}.fields.sheetNameHint`)}
                    />
                  </label>
                  <label>
                    {t(`${COPY}.fields.sharedWith`)}
                    <input
                      value={activeConfig.sharedWith}
                      onChange={(event) => updateConfig({ sharedWith: event.target.value })}
                      placeholder="data@midpoint.map"
                    />
                  </label>
                </>
              ) : null}

              {method === "document" ? (
                <>
                  <label className="cm-contrib-wizard__file">
                    <span>{t(`${COPY}.fields.file`)}</span>
                    <input
                      type="file"
                      accept=".csv,.xlsx,.xls,.json,text/csv,application/json,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                      onChange={(event) => setFile(event.target.files?.[0] || null)}
                    />
                    <small>
                      {file
                        ? t(`${COPY}.fields.fileSelected`, { name: file.name })
                        : t(`${COPY}.fields.fileHint`)}
                    </small>
                  </label>
                  <label>
                    {t(`${COPY}.fields.formatHint`)}
                    <input
                      value={activeConfig.formatHint}
                      onChange={(event) => updateConfig({ formatHint: event.target.value })}
                      placeholder={t(`${COPY}.fields.formatHintPlaceholder`)}
                    />
                  </label>
                </>
              ) : null}

              {method === "api" ? (
                <>
                  <label>
                    {t(`${COPY}.fields.baseUrl`)}
                    <input
                      value={activeConfig.baseUrl}
                      onChange={(event) => updateConfig({ baseUrl: event.target.value })}
                      placeholder="https://api.agency.com/v1"
                    />
                  </label>
                  <label>
                    {t(`${COPY}.fields.listingsPath`)}
                    <input
                      value={activeConfig.listingsPath}
                      onChange={(event) => updateConfig({ listingsPath: event.target.value })}
                    />
                  </label>
                  <div className="cm-contrib-wizard__row">
                    <label>
                      {t(`${COPY}.fields.authType`)}
                      <select
                        value={activeConfig.authType}
                        onChange={(event) => updateConfig({ authType: event.target.value })}
                      >
                        <option value="none">{t(`${COPY}.authTypes.none`)}</option>
                        <option value="api_key">{t(`${COPY}.authTypes.api_key`)}</option>
                        <option value="bearer">{t(`${COPY}.authTypes.bearer`)}</option>
                      </select>
                    </label>
                    <label>
                      {t(`${COPY}.fields.apiKeyHeader`)}
                      <input
                        value={activeConfig.apiKeyHeader}
                        onChange={(event) => updateConfig({ apiKeyHeader: event.target.value })}
                      />
                    </label>
                  </div>
                  {activeConfig.authType !== "none" ? (
                    <label>
                      {t(`${COPY}.fields.apiKeyValue`)}
                      <input
                        type="password"
                        value={activeConfig.apiKeyValue}
                        onChange={(event) => updateConfig({ apiKeyValue: event.target.value })}
                        autoComplete="new-password"
                      />
                    </label>
                  ) : null}
                  <label>
                    {t(`${COPY}.fields.docsUrl`)}
                    <input
                      value={activeConfig.docsUrl}
                      onChange={(event) => updateConfig({ docsUrl: event.target.value })}
                      placeholder="https://…"
                    />
                  </label>
                </>
              ) : null}

              {method === "data_object" ? (
                <>
                  <label>
                    {t(`${COPY}.fields.sampleObject`)}
                    <textarea
                      rows={12}
                      value={activeConfig.sampleObject}
                      onChange={(event) => updateConfig({ sampleObject: event.target.value })}
                      spellCheck={false}
                    />
                  </label>
                  <label>
                    {t(`${COPY}.fields.schemaNotes`)}
                    <textarea
                      rows={3}
                      value={activeConfig.schemaNotes}
                      onChange={(event) => updateConfig({ schemaNotes: event.target.value })}
                    />
                  </label>
                  <label>
                    {t(`${COPY}.fields.updateCadence`)}
                    <select
                      value={activeConfig.updateCadence}
                      onChange={(event) => updateConfig({ updateCadence: event.target.value })}
                    >
                      <option value="realtime">{t(`${COPY}.cadence.realtime`)}</option>
                      <option value="hourly">{t(`${COPY}.cadence.hourly`)}</option>
                      <option value="daily">{t(`${COPY}.cadence.daily`)}</option>
                      <option value="weekly">{t(`${COPY}.cadence.weekly`)}</option>
                    </select>
                  </label>
                </>
              ) : null}

              <div className="cm-contrib-wizard__row">
                <label>
                  {t(`${COPY}.fields.contactName`)}
                  <input
                    value={contactName}
                    onChange={(event) => setContactName(event.target.value)}
                  />
                </label>
                <label>
                  {t(`${COPY}.fields.contactEmail`)}
                  <input
                    type="email"
                    value={contactEmail}
                    onChange={(event) => setContactEmail(event.target.value)}
                  />
                </label>
              </div>
              <label>
                {t(`${COPY}.fields.notes`)}
                <textarea
                  rows={3}
                  value={notes}
                  onChange={(event) => setNotes(event.target.value)}
                  placeholder={t(`${COPY}.fields.notesHint`)}
                />
              </label>
            </div>
          ) : null}

          {stepId === "review" ? (
            <div className="cm-contrib-wizard__review">
              <dl>
                <div>
                  <dt>{t(`${COPY}.review.method`)}</dt>
                  <dd>{methodLabel}</dd>
                </div>
                {agencySlug ? (
                  <div>
                    <dt>{t(`${COPY}.review.agency`)}</dt>
                    <dd>
                      {agencies.find((agency) => agency.slug === agencySlug)?.name || agencySlug}
                    </dd>
                  </div>
                ) : null}
                {method === "ftp" ? (
                  <div>
                    <dt>{t(`${COPY}.review.endpoint`)}</dt>
                    <dd>
                      {activeConfig.protocol}://{activeConfig.host}:{activeConfig.port}
                      {activeConfig.remotePath}
                    </dd>
                  </div>
                ) : null}
                {method === "google_sheet" ? (
                  <div>
                    <dt>{t(`${COPY}.review.sheet`)}</dt>
                    <dd>{activeConfig.sheetUrl}</dd>
                  </div>
                ) : null}
                {method === "document" ? (
                  <div>
                    <dt>{t(`${COPY}.review.file`)}</dt>
                    <dd>{file?.name}</dd>
                  </div>
                ) : null}
                {method === "api" ? (
                  <div>
                    <dt>{t(`${COPY}.review.endpoint`)}</dt>
                    <dd>
                      {activeConfig.baseUrl}
                      {activeConfig.listingsPath}
                    </dd>
                  </div>
                ) : null}
                {method === "data_object" ? (
                  <div>
                    <dt>{t(`${COPY}.review.cadence`)}</dt>
                    <dd>{t(`${COPY}.cadence.${activeConfig.updateCadence || "daily"}`)}</dd>
                  </div>
                ) : null}
              </dl>
              <p className="muted">{t(`${COPY}.review.note`)}</p>
            </div>
          ) : null}

          {stepId === "done" ? (
            <div className="cm-contrib-wizard__done">
              <h3>{t(`${COPY}.done.title`)}</h3>
              <p>{result?.message || t(`${COPY}.done.body`)}</p>
              {result?.contribution?.contributionId ? (
                <p className="muted">
                  {t(`${COPY}.done.reference`, { id: result.contribution.contributionId })}
                </p>
              ) : null}
            </div>
          ) : null}

          {error ? (
            <p className="cm-contrib-wizard__error" role="alert">
              {error}
            </p>
          ) : null}
        </div>

        <footer className="cm-contrib-wizard__footer">
          {stepId === "done" ? (
            <button
              type="button"
              className="partner-dashboard-btn partner-dashboard-btn--primary"
              onClick={onClose}
            >
              {t(`${COPY}.done.close`)}
            </button>
          ) : (
            <>
              <button
                type="button"
                className="partner-dashboard-btn"
                onClick={() => {
                  if (stepIndex === 0) onClose?.();
                  else setStepIndex((current) => Math.max(0, current - 1));
                }}
                disabled={submitting}
              >
                {stepIndex === 0 ? t(`${COPY}.cancel`) : t(`${COPY}.back`)}
              </button>
              {stepId === "review" ? (
                <button
                  type="button"
                  className="partner-dashboard-btn partner-dashboard-btn--primary"
                  onClick={handleSubmit}
                  disabled={submitting}
                >
                  {submitting ? t(`${COPY}.submitting`) : t(`${COPY}.submit`)}
                </button>
              ) : (
                <button
                  type="button"
                  className="partner-dashboard-btn partner-dashboard-btn--primary"
                  onClick={handleNext}
                  disabled={submitting}
                >
                  {t(`${COPY}.continue`)}
                </button>
              )}
            </>
          )}
        </footer>
      </div>
    </div>
  );
}
